import '@shoelace-style/shoelace/dist/components/dialog/dialog.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import type SlDialog from '@shoelace-style/shoelace/dist/components/dialog/dialog.js';
import { View } from 'rune-ts';

export interface BaseDialogOptions {
  label?: string;
  html?: string;
  runeView?: View;
  width?: string; // 예: '400px'
  zIndex?: number; // 예: 9999
  className?: string; // sl-dialog에 class 추가
  appended: (el: HTMLElement) => void;
}

// export class DfTestView extends View {
//   override template() {
//     return html`<div>테스트</div> `;
//   }
//
//   protected override onRender() {
//     super.onRender();
//     console.log('good');
//   }
// }

const hydrationRune = (viewInstance: View, parent_el: HTMLElement) => {
  const rune_el = parent_el.querySelector('.df-util-ui-item-wrapper > [data-rune]') as HTMLElement;
  (viewInstance.constructor as typeof View).createAndHydrate(rune_el);
};

function createDialogBase(opts: BaseDialogOptions): SlDialog {
  const dialog = document.createElement('sl-dialog');
  dialog.label = opts.label || '';
  if (opts.className) dialog.classList.add(opts.className);

  const width = opts.width ?? '400px';
  const zIndex = opts.zIndex ?? 9999;

  dialog.setAttribute('style', `--width: ${width}; z-index: ${zIndex};`);
  document.body.appendChild(dialog);

  function hasVerticalScrollbar(): boolean {
    return document.documentElement.scrollHeight > document.documentElement.clientHeight;
  }

  dialog?.addEventListener('sl-show', () => {
    if (hasVerticalScrollbar()) {
      document.documentElement.style.setProperty('scrollbar-gutter', 'stable', 'important');
    } else {
      document.documentElement.style.setProperty('scrollbar-gutter', 'auto', 'important');
    }
  });

  dialog?.addEventListener('sl-after-hide', () => {
    // 모달 닫힌 뒤 원상 복구
    document.documentElement.style.removeProperty('scrollbar-gutter');
  });

  return dialog;
}

function makeHtml({ html, runeView }: { html?: string; runeView?: View }) {
  if (html) return html;

  if (runeView) return runeView.toHtmlSSR();
  throw new Error('html, runeView not found!');
}

function alert(options: BaseDialogOptions): Promise<void> {
  return new Promise((resolve) => {
    const dialog = createDialogBase(options);

    dialog.innerHTML = `
      <div class="df-util-ui-item-wrapper">
      ${makeHtml(options)}
</div>
      <sl-button slot="footer" variant="primary" autofocus>확인</sl-button>
    `;
    if (options.runeView) {
      hydrationRune(options.runeView, dialog);
    }
    dialog.querySelector('sl-button')!.addEventListener('click', () => {
      dialog.hide();
    });

    dialog.addEventListener('sl-after-hide', () => {
      dialog.remove();
      resolve();
    });

    requestAnimationFrame(() => dialog.show());
  });
}

function confirm(options: BaseDialogOptions): Promise<boolean | null> {
  return new Promise((resolve) => {
    const dialog = createDialogBase(options);

    dialog.innerHTML = `
      <div class="df-util-ui-item-wrapper">
      ${makeHtml(options)}
</div>
      <sl-button slot="footer" variant="primary" id="ok-btn">확인</sl-button>
      <sl-button slot="footer" variant="default" id="cancel-btn">취소</sl-button>
    `;
    if (options.runeView) {
      hydrationRune(options.runeView, dialog);
    }
    const ok = dialog.querySelector('#ok-btn')!;
    const cancel = dialog.querySelector('#cancel-btn')!;

    let resolved = false;
    ok.addEventListener('click', () => {
      if (!resolved) {
        resolved = true;
        dialog.hide();
        resolve(true);
      }
    });

    cancel.addEventListener('click', () => {
      if (!resolved) {
        resolved = true;
        dialog.hide();
        resolve(false);
      }
    });

    dialog.addEventListener('sl-after-hide', () => {
      if (!resolved) {
        resolved = true;
        resolve(null); // X버튼, 백드롭, ESC 등으로 닫힌 경우
      }
      dialog.remove();
    });

    requestAnimationFrame(() => dialog.show());
  });
}

function modal(options: BaseDialogOptions): Promise<void> {
  return new Promise((resolve) => {
    const dialog = createDialogBase(options);

    dialog.innerHTML = `
      <div class="df-util-ui-item-wrapper">
      ${makeHtml(options)}
      </div>
    `;
    if (options.appended) {
      options.appended(dialog);
    }
    if (options.runeView) {
      hydrationRune(options.runeView, dialog);
    }

    dialog.addEventListener('sl-after-hide', () => {
      dialog.remove();
      resolve();
    });

    requestAnimationFrame(() => dialog.show());
  });
}

export const DfUtilUi = {
  alert,
  confirm,
  modal,
};
